import { BaseCommand } from '../ICommand';
import { getNodeMutationManager } from '../mutations/NodeMutationManager';

interface Point {
  x: number;
  y: number;
}

interface FreeLinePoints {
  fromPoint: Point;
  toPoint: Point;
}

const translateFlatPointArray = (value: unknown, deltaX: number, deltaY: number): number[] | undefined => {
  if (!Array.isArray(value) || value.length < 2 || value.length % 2 !== 0) return undefined;
  const shifted: number[] = [];
  for (let i = 0; i < value.length; i += 2) {
    const x = Number(value[i]);
    const y = Number(value[i + 1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined;
    shifted.push(x + deltaX, y + deltaY);
  }
  return shifted;
};

const translateWaypointArray = (
  value: unknown,
  deltaX: number,
  deltaY: number,
): Array<Record<string, unknown>> | undefined => {
  if (!Array.isArray(value)) return undefined;
  const shifted: Array<Record<string, unknown>> = [];
  for (const waypoint of value) {
    if (!waypoint || typeof waypoint !== 'object') return undefined;
    const record = waypoint as Record<string, unknown>;
    const x = Number(record.x);
    const y = Number(record.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined;
    shifted.push({
      ...record,
      x: x + deltaX,
      y: y + deltaY,
    });
  }
  return shifted;
};

export class MoveFreeLineCommand extends BaseCommand {
  private edgeId: string;
  private oldPoints: FreeLinePoints;
  private newPoints: FreeLinePoints;

  constructor(edgeId: string, oldPoints: FreeLinePoints, newPoints: FreeLinePoints) {
    super('Move Free Line', `Move free line ${edgeId}`);
    this.edgeId = edgeId;
    this.oldPoints = oldPoints;
    this.newPoints = newPoints;
  }

  private getDelta(from: FreeLinePoints, to: FreeLinePoints): { x: number; y: number } {
    return {
      x: to.fromPoint.x - from.fromPoint.x,
      y: to.fromPoint.y - from.fromPoint.y,
    };
  }

  private buildTranslatedUpdate(
    current: Record<string, unknown> | undefined,
    from: FreeLinePoints,
    to: FreeLinePoints,
  ): Record<string, unknown> {
    const delta = this.getDelta(from, to);
    const baseAttrs = ((current?.attrs as Record<string, unknown>) || {}) as Record<string, unknown>;
    const attrs: Record<string, unknown> = {
      ...baseAttrs,
      fromPoint: to.fromPoint,
      toPoint: to.toPoint,
      routingType: baseAttrs.routingType ?? 'straight',
    };

    const shiftedLayoutPoints = translateFlatPointArray(baseAttrs.layoutPoints, delta.x, delta.y);
    if (shiftedLayoutPoints) attrs.layoutPoints = shiftedLayoutPoints;

    const shiftedAttrWaypoints = translateWaypointArray(baseAttrs.waypoints, delta.x, delta.y);
    if (shiftedAttrWaypoints) attrs.waypoints = shiftedAttrWaypoints;

    const updates: Record<string, unknown> = { attrs };
    const shiftedPoints = translateFlatPointArray(current?.points, delta.x, delta.y);
    if (shiftedPoints) updates.points = shiftedPoints;

    const shiftedWaypoints = translateWaypointArray(current?.waypoints, delta.x, delta.y);
    if (shiftedWaypoints) updates.waypoints = shiftedWaypoints;

    return updates;
  }

  execute(): void {
    const mutationManager = getNodeMutationManager();
    const current = mutationManager.getEdge(this.edgeId) as Record<string, unknown> | undefined;
    mutationManager.updateEdge(this.edgeId, this.buildTranslatedUpdate(current, this.oldPoints, this.newPoints) as any);
  }

  undo(): void {
    const mutationManager = getNodeMutationManager();
    const current = mutationManager.getEdge(this.edgeId) as Record<string, unknown> | undefined;
    mutationManager.updateEdge(this.edgeId, this.buildTranslatedUpdate(current, this.newPoints, this.oldPoints) as any);
  }
}
